import { createSlice, createAction, createSelector } from '@reduxjs/toolkit'

const initialState = {
  isLoading: true,
  isAuth: false,
  error: {
    message: '',
    code: '',
  },
  userData: {
    phone_number: '',
    email: '',
    id: '',
    first_name: '',
    last_name: '',
    works_in: {},
    image_uri: '',
    is_superuser: false,
    groups: [],
    locations: [],
  },
  isFetching: false,
  restorePasswordEmailString: '',
  isSuccessRestorePassword: false,
  errorRestorePassword: {
    type: null,
  },
  newPasswordRequired: null,
  successChangedData: {
    message: 'Your data has been changed!',
    isShow: false,
    timeout: 2000,
  },
  changePasswordError: {
    code: '',
  },
  redirectAfterLogout: null,
  successSendFormData: {
    message: 'We’ll contact you in 24 hours.',
    isShow: false,
    timeout: 2000,
  },
}

export const getGroupIdByFacilityIdSelector = (facilityId) => (state) => {
  return (
    state.profile.userData.locations.find(
      (location) => location?.id === facilityId
    )?.group?.id || null
  )
}

export const getProfileUserDataIdSelector = state => state.profile.userData.id

export const getProfileGroupsSelector = (state) => state.profile.userData.groups

export const getIsSuperUserSelector = (state) =>
  state.profile.userData.is_superuser
export const getInitialsOfNameSelector = (state) => {
  const fN = state.profile?.userData?.first_name
  const lN = state.profile?.userData?.last_name
  return `${fN[0] || ''}${lN[0] || ''}`
}
export const getRedirectAfterLogoutSelector = (state) =>
  state.profile.redirectAfterLogout
export const getChangePasswordErrorCodeSelector = (state) =>
  state.profile.changePasswordError.code
export const getSuccessChangedDataSelector = (state) =>
  state.profile.successChangedData
export const getSuccessSendFormData = (state) =>
    state.profile.successSendFormData
export const getRestorePasswordEmailString = (state) =>
  state.profile.restorePasswordEmailString
export const getIsLoadingSelector = (state) => state.profile.isLoading
export const getIsAuthSelector = (state) => state.profile.isAuth
export const getIsFetchingSelector = (state) => state.profile.isFetching
export const getErrorMessageSelector = (state) => state.profile.error.message

export const getErrorRestoreTypeSelector = (state) =>
  state.profile.errorRestorePassword.type
export const getIsSuccessResporePassword = (state) =>
  state.profile.isSuccessRestorePassword
export const getNewPasswordRequiredDataSelector = (state) =>
  state.profile.newPasswordRequired
export const getProfileDataSelector = (state) => state.profile.userData
export const getProfileAvatarSelector = (state) =>
  state.profile.userData?.image_uri

export const getGroupDataSelector = (id) => (state) => {
  return state.profile?.userData?.groups.find((group) => group?.id === id)
}

export const getFacilityCountSelector = (id) => (state) =>
  state.profile.userData?.locations?.filter(
    (location) => location?.group?.id === id
  )?.length || 0

export const selectLocationsById = (groupId) => {
  return createSelector(getProfileDataSelector, (userData) => {
    const { locations } = userData
    if (!locations || !Array.isArray(locations) || !locations.length) {
      return null
    }
    const locationsList = locations
      .filter((item) => item?.group?.id === groupId)
      .map((item) => ({
        id: item.id,
        locationName: item.name,
      }))
    return locationsList
  })
}

export const getLocationByIdSelector = (facilityId) => {
  return createSelector(getProfileDataSelector, (userData) => {
    const { locations = [] } = userData

    return locations.find((l) => l.id === facilityId) || {}
  })
}

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setIsLoading(state, action) {
      state.isLoading = action.payload
    },
    setIsAuth(state, action) {
      state.isAuth = action.payload
    },
    setError(state, action) {
      state.error = action.payload
    },
    setIsFetching(state, action) {
      state.isFetching = action.payload
    },
    logout(state, action) {
      state.isAuth = initialState.isAuth
      state.userData = initialState.userData
      state.redirectAfterLogout = action.payload
    },
    clearRedirectAfterLogout(state) {
      state.redirectAfterLogout = null
    },
    setRestorePasswordEmailString(state, action) {
      state.restorePasswordEmailString = action.payload
    },
    setIsSuccessResporePassword(state, action) {
      state.isSuccessRestorePassword = action.payload
    },
    setErrorResporePasswordType(state, action) {
      state.errorRestorePassword = action.payload
    },
    clearError(state) {
      state.error = initialState.error
    },
    setNewPasswordRequiredData(state, action) {
      state.newPasswordRequired = action.payload
    },
    clearNewPasswordRequiredData(state) {
      state.newPasswordRequired = initialState.newPasswordRequired
    },
    getUserProfile(state) {
      state.error = initialState.error
    },
    setUserData(state, action) {
      state.userData = action.payload
    },
    setSuccessChangedData(state, action) {
      state.successChangedData.message =
        action.payload.message || initialState.successChangedData.message
      state.successChangedData.isShow = action.payload.isShow
      state.successChangedData.timeout =
        action.payload.timeout || initialState.successChangedData.timeout
    },
    setChangePasswordErrorCode(state, action) {
      state.changePasswordError.code = action.payload
    },
    clearChangePasswordErrorCode(state) {
      state.changePasswordError.code = ''
    },
    clearProfile(state) {
      state.changePasswordError = initialState.changePasswordError
      state.error = initialState.error
      state.successChangedData = initialState.successChangedData
    },
    setProfileAvatar(state, action) {
      state.userData.image_uri = action.payload
    },
    setSuccessSendFormData(state, action) {
      state.successSendFormData.message =
          action.payload.message || initialState.successSendFormData.message
      state.successSendFormData.isShow = action.payload.isShow
      state.successSendFormData.timeout =
          action.payload.timeout || initialState.successSendFormData.timeout
    },
  },
})

export const loginUser = createAction('profile/login')
export const getSession = createAction('profile/getSession')
export const restorePassword = createAction('profile/restorePassword')
export const changePassword = createAction('profile/changePassword')
export const editProfile = createAction('profile/edit')
export const changeCurrentPassword = createAction(
  'profile/changeCurrentPassword'
)
export const sendProfileAvatar = createAction('profile/sendProfileAvatar')
export const sendHelpSupportForm = createAction('profile/sendHelpSupportForm')

export const {
  setIsLoading,
  setIsAuth,
  setError,
  setUserData,
  setIsFetching,
  logout,
  setRestorePasswordEmailString,
  setIsSuccessResporePassword,
  setErrorResporePasswordType,
  clearError,
  setNewPasswordRequiredData,
  clearNewPasswordRequiredData,
  getUserProfile,
  setSuccessChangedData,
  setChangePasswordErrorCode,
  clearChangePasswordErrorCode,
  clearProfile,
  setProfileAvatar,
  clearRedirectAfterLogout,
  setSuccessSendFormData,
} = profileSlice.actions

export default profileSlice.reducer
